import {DataSourceFootnote} from "./DataSourceFootnote";

import type {XeroPnLMonthlyPerf} from "@hooks/use-vnext";

interface XeroPnlMonthlyPerformanceProps {
    months: XeroPnLMonthlyPerf[];
    generatedAt?: string;
    showFootnote?: boolean;
}

function fmtCurrency(v: number): string {
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
    return `$${v.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
}

export function XeroPnlMonthlyPerformance({months, generatedAt, showFootnote = true}: XeroPnlMonthlyPerformanceProps) {
    if (!months.length) return null;

    const maxRevenue = Math.max(...months.map((m) => Math.abs(m.revenue)), 1);

    return (
        <div>
            <h3 className="text-sm font-bold uppercase tracking-[2px] text-[#209DB3] mb-3">
                Monthly Performance
            </h3>
            {/* CSS sparkline bars */}
            <div className="flex items-end gap-[2px] h-12 mb-4 px-3">
                {months.map((m) => {
                    const h = maxRevenue > 0 ? (Math.abs(m.revenue) / maxRevenue) * 100 : 0;
                    return (
                        <div key={m.month_key} className="flex-1 flex flex-col items-center">
                            <div
                                className={`w-full rounded-t-sm ${
                                    m.status === "actual" ? "bg-[#209DB3]" : "bg-[#209DB3]/30"
                                }`}
                                style={{height: `${h}%`}}
                            />
                            <span className="text-[8px] text-muted-foreground mt-0.5">
                                {new Date(Number(m.month_key.slice(0, 4)), Number(m.month_key.slice(5)) - 1).toLocaleString("en-US", {month: "short"})}
                            </span>
                        </div>
                    );
                })}
            </div>
            <div className="overflow-x-auto border rounded-lg">
                <table className="text-xs" style={{minWidth: "max(100%, 700px)"}}>
                    <thead>
                        <tr className="bg-[#209DB3]/10 border-b">
                            <th className="text-left px-3 py-2 font-medium text-[#209DB3]">Month</th>
                            <th className="text-center px-3 py-2 font-medium text-[#209DB3]">Status</th>
                            <th className="text-right px-3 py-2 font-medium text-[#209DB3]">Revenue</th>
                            <th className="text-right px-3 py-2 font-medium text-[#209DB3]">COGS</th>
                            <th className="text-right px-3 py-2 font-medium text-[#209DB3]">Gross Profit</th>
                            <th className="text-right px-3 py-2 font-medium text-[#209DB3]">Net Profit</th>
                            <th className="text-right px-3 py-2 font-medium text-[#209DB3]">GM%</th>
                            <th className="text-right px-3 py-2 font-medium text-[#209DB3]">NM%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {months.map((m) => (
                            <tr key={m.month_key} className="border-b border-gray-100 hover:bg-gray-50/50">
                                <td className="px-3 py-1.5 font-medium">{m.month_key}</td>
                                <td className="text-center px-3 py-1.5">
                                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                        m.status === "actual"
                                            ? "bg-[#209DB3]/10 text-[#209DB3]"
                                            : "bg-amber-100 text-amber-700"
                                    }`}>
                                        {m.status === "actual" ? "Actual" : "Forecast"}
                                    </span>
                                </td>
                                <td className="text-right px-3 py-1.5 tabular-nums">
                                    {fmtCurrency(m.revenue)}
                                </td>
                                <td className="text-right px-3 py-1.5 tabular-nums">
                                    {fmtCurrency(m.costs)}
                                </td>
                                <td className={`text-right px-3 py-1.5 tabular-nums font-medium ${m.gross_profit < 0 ? "text-red-600" : ""}`}>
                                    {fmtCurrency(m.gross_profit)}
                                </td>
                                <td className={`text-right px-3 py-1.5 tabular-nums font-medium ${m.net_profit < 0 ? "text-red-600" : ""}`}>
                                    {fmtCurrency(m.net_profit)}
                                </td>
                                <td className="text-right px-3 py-1.5 tabular-nums">
                                    {m.gross_margin_pct != null ? `${m.gross_margin_pct}%` : "\u2014"}
                                </td>
                                <td className={`text-right px-3 py-1.5 tabular-nums ${
                                    m.net_margin_pct != null && m.net_margin_pct < 0 ? "text-red-600" : ""
                                }`}>
                                    {m.net_margin_pct != null ? `${m.net_margin_pct}%` : "\u2014"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {showFootnote && <DataSourceFootnote generatedAt={generatedAt} />}
        </div>
    );
}
